/**
 * Date formatting and parsing.
 *
 * Storage convention: ISO-8601 strings at the API boundary. Display convention
 * for UAT: day-month-year order everywhere, using `DD-MM-YYYY` for dates and
 * `DD-MM-YYYY HH:MM` for date+time values.
 *
 * Time zone: display assumes Asia/Karachi (UTC+5).
 */

const PKT_OFFSET_MINUTES = 5 * 60; // UTC+5

const MONTH_ABBREV = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

const MONTH_INDEX: Record<string, number> = {
  jan: 1,
  january: 1,
  feb: 2,
  february: 2,
  mar: 3,
  march: 3,
  apr: 4,
  april: 4,
  may: 5,
  jun: 6,
  june: 6,
  jul: 7,
  july: 7,
  aug: 8,
  august: 8,
  sep: 9,
  sept: 9,
  september: 9,
  oct: 10,
  october: 10,
  nov: 11,
  november: 11,
  dec: 12,
  december: 12,
};

/**
 * Format an ISO date string (or Date) as `DD-MM-YYYY` in PKT.
 *
 * Returns an em dash for null/undefined/empty so tables can use this directly:
 *   formatDate(p.deliveryTarget)   // "12-03-2026" or "—"
 */
export function formatDate(input: string | Date | null | undefined): string {
  const parts = getPktDateParts(input);
  if (!parts) return '—';
  return `${parts.day}-${parts.month}-${parts.year}`;
}

/**
 * Long date display. Kept as a separate export for existing call sites, but it
 * now follows the same UAT-wide `DD-MM-YYYY` convention as `formatDate`.
 */
export function formatDateLong(input: string | Date | null | undefined): string {
  return formatDate(input);
}

/**
 * Optional text-month display for places that explicitly need it while still
 * keeping day-month-year order.
 */
export function formatDateTextMonth(input: string | Date | null | undefined): string {
  const parts = getPktDateParts(input);
  if (!parts) return '—';
  const monthLabel = MONTH_ABBREV[Number(parts.month) - 1];
  return `${parts.day} ${monthLabel} ${parts.year}`;
}

/**
 * Format a date+time as `DD-MM-YYYY HH:MM` in PKT — used for audit log entries.
 */
export function formatDateTime(input: string | Date | null | undefined): string {
  if (input == null || input === '') return '—';
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return '—';

  const pkt = shiftToPkt(date);
  const day = pkt.getUTCDate().toString().padStart(2, '0');
  const month = (pkt.getUTCMonth() + 1).toString().padStart(2, '0');
  const year = pkt.getUTCFullYear();
  const hours = pkt.getUTCHours().toString().padStart(2, '0');
  const minutes = pkt.getUTCMinutes().toString().padStart(2, '0');
  return `${day}-${month}-${year} ${hours}:${minutes}`;
}

/**
 * Parse direct user-entered date text into an ISO date-only string.
 *
 * Accepted examples:
 *   28-05-2026
 *   28/05/2026
 *   28.05.2026
 *   28 May 2026
 *   28-May-2026
 *   2026-05-28
 *
 * Ambiguous slash/dash/dot dates are interpreted as day-month-year, never
 * month-day-year.
 */
export function parseDateDMY(input: string | null | undefined): string | null {
  if (input == null) return null;
  const value = input.trim();
  if (value === '') return null;

  const isoMatch = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(value);
  if (isoMatch) {
    return normaliseDateParts(Number(isoMatch[1]!), Number(isoMatch[2]!), Number(isoMatch[3]!));
  }

  const numericDmyMatch = /^(\d{1,2})[\-/\.](\d{1,2})[\-/\.](\d{2}|\d{4})$/.exec(value);
  if (numericDmyMatch) {
    const year = normaliseYear(Number(numericDmyMatch[3]!));
    return normaliseDateParts(year, Number(numericDmyMatch[2]!), Number(numericDmyMatch[1]!));
  }

  const textMonthMatch = /^(\d{1,2})[\s-]+([A-Za-z]{3,9})[\s,-]+(\d{2}|\d{4})$/.exec(value);
  if (textMonthMatch) {
    const monthToken = textMonthMatch[2]!;
    const month = MONTH_INDEX[monthToken.toLowerCase()];
    if (!month) return null;
    const year = normaliseYear(Number(textMonthMatch[3]!));
    return normaliseDateParts(year, month, Number(textMonthMatch[1]!));
  }

  return null;
}

/** Alias for readability at form boundaries. */
export const parseDateInput = parseDateDMY;

/**
 * Calculate the integer day difference between two dates (today, by default).
 *
 *   daysBetween('2026-03-10', '2026-03-15')  // 5
 *   daysBetween('2026-03-15', '2026-03-10')  // -5
 */
export function daysBetween(earlier: string | Date, later: string | Date = new Date()): number {
  const a = earlier instanceof Date ? earlier : new Date(earlier);
  const b = later instanceof Date ? later : new Date(later);
  const diffMs = b.getTime() - a.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

/**
 * Relative time string for feeds and notifications.
 *
 *   "Just now", "3 minutes ago", "2 hours ago", "Yesterday", "3 days ago", "12-03-2026"
 */
export function formatRelative(input: string | Date | null | undefined): string {
  if (input == null || input === '') return '—';
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return '—';

  const now = new Date();
  const diffSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffSeconds < 30) return 'Just now';
  if (diffSeconds < 60) return `${diffSeconds} seconds ago`;
  if (diffSeconds < 3600) {
    const minutes = Math.floor(diffSeconds / 60);
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  }
  if (diffSeconds < 86_400) {
    const hours = Math.floor(diffSeconds / 3600);
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }
  if (diffSeconds < 86_400 * 2) return 'Yesterday';
  if (diffSeconds < 86_400 * 7) {
    const days = Math.floor(diffSeconds / 86_400);
    return `${days} days ago`;
  }
  // Beyond a week: switch to absolute short form
  return formatDate(date);
}

/**
 * Today's date as ISO date-only (YYYY-MM-DD) in PKT.
 */
export function todayPktIsoDate(): string {
  const now = new Date();
  const pkt = shiftToPkt(now);
  const year = pkt.getUTCFullYear();
  const month = (pkt.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = pkt.getUTCDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Add an integer number of months to an ISO date, clamping the day-of-month
 * to the last valid day of the target month (Patch E14).
 *
 * JavaScript's `Date.prototype.setMonth(getMonth() + n)` is wrong for date
 * arithmetic where the source day-of-month doesn't exist in the target month
 * — it rolls forward into the following month instead of clamping back.
 * Concrete example: a 12-month warranty starting `2025-01-31` should end on
 * `2026-01-31` (fine — Jan has 31), but a 1-month warranty starting
 * `2026-01-31` should end on `2026-02-28`, not `2026-03-03` which is what
 * setMonth produces because Feb 31 doesn't exist and rolls over.
 *
 *   addMonthsClamped('2026-01-31',  1) === '2026-02-28'
 *   addMonthsClamped('2024-02-29', 12) === '2025-02-28'
 *   addMonthsClamped('2025-01-31', 12) === '2026-01-31'
 *
 * Affects: deriveWarrantyEndDate (§10.4), notification-scanner BG_RELEASE_OVERDUE
 * and WARRANTY_END_APPROACHING detectors.
 */
export function addMonthsClamped(iso: string, months: number): string {
  // Parse the ISO date as UTC date-only. We don't want local-timezone parsing
  // to shift the day backward when the host is east of UTC.
  const [yStr, mStr, dStr] = iso.slice(0, 10).split('-');
  if (!yStr || !mStr || !dStr) {
    throw new Error(`addMonthsClamped: not an ISO date — ${iso}`);
  }
  const sourceYear = Number(yStr);
  const sourceMonth = Number(mStr) - 1; // JS months: 0–11
  const sourceDay = Number(dStr);
  if (!Number.isFinite(sourceYear) || !Number.isFinite(sourceMonth) || !Number.isFinite(sourceDay)) {
    throw new Error(`addMonthsClamped: not an ISO date — ${iso}`);
  }

  // Total month offset from year 0 to make negative `months` work correctly
  // without resorting to JS's modulo-of-negative quirks.
  const totalMonths = sourceYear * 12 + sourceMonth + months;
  const targetYear = Math.floor(totalMonths / 12);
  const targetMonth = totalMonths - targetYear * 12;

  // Last day of the target month: day 0 of the *next* month rolls back to it.
  const lastDayOfTargetMonth = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
  const clampedDay = Math.min(sourceDay, lastDayOfTargetMonth);

  const y = targetYear.toString().padStart(4, '0');
  const m = (targetMonth + 1).toString().padStart(2, '0');
  const d = clampedDay.toString().padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getPktDateParts(input: string | Date | null | undefined): { day: string; month: string; year: string } | null {
  if (input == null || input === '') return null;
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return null;

  const pkt = shiftToPkt(date);
  return {
    day: pkt.getUTCDate().toString().padStart(2, '0'),
    month: (pkt.getUTCMonth() + 1).toString().padStart(2, '0'),
    year: pkt.getUTCFullYear().toString().padStart(4, '0'),
  };
}

function normaliseYear(year: number): number {
  if (year < 100) return year >= 70 ? 1900 + year : 2000 + year;
  return year;
}

function normaliseDateParts(year: number, month: number, day: number): string | null {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;
  if (year < 1900 || year > 9999 || month < 1 || month > 12 || day < 1 || day > 31) return null;

  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  const y = year.toString().padStart(4, '0');
  const m = month.toString().padStart(2, '0');
  const d = day.toString().padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Apply PKT offset to a UTC instant for display purposes.
 *
 * Returns a Date whose UTC components correspond to the local PKT components
 * of the input. This is the simplest way to extract local date/time pieces
 * without bringing in a timezone library.
 */
function shiftToPkt(date: Date): Date {
  return new Date(date.getTime() + PKT_OFFSET_MINUTES * 60_000);
}
